import {
  useUpdateWorkflowStatusMutation,
  useWorkflowStagesAndZonesQuery,
  useWorkflowStagesLazyQuery,
  Zone,
  ZoneState,
} from '@/__generated__/graphql';
import * as state from '@/state';
import { useCallback, useEffect, useMemo } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import { WorkflowStageColors } from '@/theme/colors';
import { FINAL_WORKFLOW_STAGE, transitionableToStartStates } from '@/constants';
import { useGetStructureId } from './useGetStructureId';

export const useWorkflowStages = () => {
  const [workflowStages, setWorkflowStages] = useAtom(state.workflowStages);
  const [currentStage, setCurrentStage] = useAtom(state.currentWorkflowStage);
  const [selectedZone, setSelectedZone] = useAtom(state.selectedZone);
  const setZones = useSetAtom(state.zones);
  const structureId = useGetStructureId();

  const [fetchStages] = useWorkflowStagesLazyQuery({
    variables: { structureId },
    onCompleted: (data) => {
      if (data.structure?.workflowStages) {
        const sortedStages = [...data.structure.workflowStages]
          .filter((stage): stage is NonNullable<typeof stage> => stage !== null)
          .sort((a, b) => (a.orderInWorkflow ?? 0) - (b.orderInWorkflow ?? 0));
        setWorkflowStages(sortedStages);
      }
    },
  });

  useEffect(() => {
    if (structureId && workflowStages.length === 0) {
      fetchStages();
    }
  }, [fetchStages, structureId, workflowStages]);

  const [updateWorkflowStatus, { loading: updatingWorkflowStatus }] =
    useUpdateWorkflowStatusMutation({
      variables: {
        input: {
          structureId: selectedZone?.structureId ?? '',
          zoneIds: [selectedZone?.id ?? ''],
        },
      },
    });

  useWorkflowStagesAndZonesQuery({
    variables: { structureId },
    skip: !structureId,
    onCompleted: (data) => {
      if (data.structure?.workflowStages) {
        const sortedStages = [...data.structure.workflowStages]
          .filter((stage): stage is NonNullable<typeof stage> => stage !== null)
          .sort((a, b) => (a.orderInWorkflow ?? 0) - (b.orderInWorkflow ?? 0));
        setWorkflowStages(sortedStages);
      }
      if (data.zonesForStructure) {
        setZones(data.zonesForStructure);
      }
    },
  });

  const isTransitionableToStartState = useMemo(() => {
    const currentState = selectedZone?.workflowStatus?.state;
    return Boolean(currentState && currentStage && transitionableToStartStates.has(currentState));
  }, [currentStage, selectedZone]);

  const isFinalStage = useMemo(() => {
    return currentStage?.name === FINAL_WORKFLOW_STAGE;
  }, [currentStage]);

  useEffect(() => {
    const stage = workflowStages.find(
      (stage) => stage.id === selectedZone?.workflowStatus?.workflowStageId
    );
    setCurrentStage(stage);
  }, [workflowStages, selectedZone, setCurrentStage]);

  const stageColors =
    currentStage?.name === FINAL_WORKFLOW_STAGE
      ? WorkflowStageColors.at(-1)
      : WorkflowStageColors.at((currentStage?.orderInWorkflow ?? 0) - 1);

  const onStatusUpdate = useCallback(
    (data: Zone[]) => {
      if (data) {
        setZones((prev) => {
          if (!prev || !Array.isArray(prev)) return [] as Zone[];
          return prev.map((zone) => {
            const updatedZone = data?.find((newZone) => newZone?.id === zone.id);
            if (!updatedZone) return zone;
            return {
              ...zone,
              workflowStatus: {
                ...zone.workflowStatus,
                ...updatedZone?.workflowStatus,
              },
            };
          }) as Zone[];
        });

        setSelectedZone((prev) => {
          if (!prev) return prev;
          const updatedZone = data?.find((zone) => zone?.id === prev.id);
          if (!updatedZone) return prev;
          return {
            ...prev,
            workflowStatus: {
              ...prev.workflowStatus,
              ...updatedZone?.workflowStatus,
            },
          } as Zone;
        });
      }
    },
    [setZones, setSelectedZone]
  );

  const workflowUpdateHandler = useCallback(
    async (state: ZoneState) => {
      if (updatingWorkflowStatus) return;
      if (!selectedZone) return;
      const workflowStatus = selectedZone.workflowStatus;
      if (!workflowStatus) return;
      const currentState = workflowStatus.state;
      if (!currentState) return;

      await updateWorkflowStatus({
        variables: {
          input: {
            structureId: selectedZone.structureId,
            zoneIds: [selectedZone.id],
            state,
          },
        },
        onCompleted: (data) => {
          if (data.updateWorkflowStatus) {
            const updatedData = (data.updateWorkflowStatus ?? []).filter(
              (zone): zone is Zone => zone !== null
            );
            onStatusUpdate(updatedData);
          }
        },
      });
    },
    [onStatusUpdate, selectedZone, updateWorkflowStatus, updatingWorkflowStatus]
  );

  return {
    currentStage,
    stageColors,
    workflowStages,
    workflowUpdateHandler,
    updatingStatus: updatingWorkflowStatus,
    isTransitionableToStartState,
    selectedZone,
    isFinalStage,
  };
};
